import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  CommonService,
  ExtendedCookieHelperUtilsService,
  LoggingService,
  MultiCityRecentSearchService,
  MultiCitySearchCache,
  RetrieveSearch,
  RetrieveSearchService
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { QueryStringHelper } from '@com-delta-idp/mnx-sitewide-shared-utils';
import { Observable } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';

@Injectable()
export abstract class HeaderSlimDeepLinkBaseService {
  commonService = inject(CommonService);
  queryStringHelper = inject(QueryStringHelper);
  _route = inject(ActivatedRoute);
  cookieHelper = inject(ExtendedCookieHelperUtilsService);
  retrieveSearchService = inject(RetrieveSearchService);
  multiCityRecentSearchService = inject(MultiCityRecentSearchService);
  loggingService = inject(LoggingService);

  deepLinkXssid = '';
  deepLinkCacheKey = '';
  deepLinkVendor = '';

  protected createPostAndRetrieveStream(
    recentData: MultiCitySearchCache | RetrieveSearch | undefined
  ): Observable<RetrieveSearch[]> {
    return recentData && Object.keys(recentData).length > 0
      ? this.retrieveSearchService
          .postRecentSearchesForDeepLink(
            recentData as RetrieveSearch,
            'searchType=RecentSearchesJSON'
          )
          .pipe(
            take(1),
            tap(() => {
              const cookieDomain = this.cookieHelper.defaultCookieDomain();

              this.cookieHelper.setCookieName(
                this.deepLinkCacheKey,
                this.deepLinkXssid,
                1,
                true,
                '/',
                cookieDomain
              );
            }),
            switchMap(() => {
              this.cookieHelper.deleteCookie('deepLinkSlimHeaderValue');
              return this.retrieveSearchService.getRetrieveSearchDeepLinkContent$(
                `&cacheBustParam=${new Date().getTime()}`
              );
            })
          )
      : (() => {
          this.cookieHelper.deleteCookie('deepLinkSlimHeaderValue');
          return this.retrieveSearchService.getRetrieveSearchDeepLinkContent$(
            `&cacheBustParam=${new Date().getTime()}`
          );
        })();
  }

  protected setDeepLinkParams(): void {
    try {
      this.setParamValues();
    } catch (error) {
      this.loggingService.log(JSON.stringify(error));
      this.resetParamValues();
    }
  }

  protected setParamValues(): void {
    this.deepLinkXssid = this.getQueryParams('deepLinkXssid') || '';
    this.deepLinkCacheKey = this.getQueryParams('cacheKeySuffix') || '';
    this.deepLinkVendor = this.getQueryParams('vendorID') || '';
  }

  protected resetParamValues(): void {
    this.deepLinkXssid = '';
    this.deepLinkCacheKey = '';
    this.deepLinkVendor = '';
  }

  protected getCookieVendor(deepLinkVendor: string): string | null {
    try {
      if (
        this.deepLinkCacheKey !==
        this.cookieHelper.getCookieValue('deepLinkCacheKey')
      )
        return null;
      const vendorCookie = this.cookieHelper.getCookieValue(
        'deepLinkVendor' + this.deepLinkCacheKey
      );
      return vendorCookie === deepLinkVendor ? vendorCookie : null;
    } catch (error) {
      this.loggingService.log(JSON.stringify(error));
      return null;
    }
  }

  protected getQueryParams(queryParamName: string): string {
    try {
      return (
        this._route.snapshot.queryParams?.[queryParamName] ||
        this.queryStringHelper.getParameterByName(queryParamName) ||
        ''
      );
    } catch (error) {
      this.loggingService.log(JSON.stringify(error));
      return '';
    }
  }
}
